import { useRef } from "react";
import { motion, useInView, animate } from "framer-motion";
import { useEffect, useState } from "react";

// Animated count-up big number. `value` may be like "57", "52.1", "20-60".
// Rhythmic "spotlight" that pops one stat big, then sucks it back as the next lights up.
export function EvidenceStats({ stats }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [focus, setFocus] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setFocus((f) => (f + 1) % stats.length), 2200);
    return () => clearInterval(id);
  }, [inView, stats.length]);

  return (
    <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mt-16">
      {stats.map((s, i) => {
        const active = i === focus && inView;
        return (
          <motion.div
            key={s.value}
            data-testid={s.testid}
            className="origin-left"
            animate={
              active
                ? { scale: [1, 1.22, 1.12], opacity: 1 }
                : { scale: 1, opacity: 0.4 }
            }
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "left center" }}
          >
            <BigStat value={s.value} caption={s.caption} color={s.color} size="lg" glow={active} run={inView} />
          </motion.div>
        );
      })}
    </div>
  );
}

export function BigStat({ value, suffix = "%", caption, color = "#00e5ff", size = "lg", testid, glow = false, run }) {
  const ref = useRef(null);
  const ownInView = useInView(ref, { amount: 0.5, once: true });
  const inView = run !== undefined ? (run || ownInView) : ownInView;
  const isRange = String(value).includes("-");
  const [a, b] = isRange ? String(value).split("-") : [value, null];
  const sizeCls =
    size === "xl"
      ? "text-7xl sm:text-8xl md:text-9xl"
      : size === "lg"
      ? "text-6xl sm:text-7xl"
      : "text-5xl sm:text-6xl";

  return (
    <div ref={ref} data-testid={testid} className="flex flex-col">
      <div
        className={`font-heading font-light tracking-tighter leading-none transition-[text-shadow] duration-500 ${sizeCls}`}
        style={{ color, textShadow: glow ? `0 0 45px ${color}80, 0 0 12px ${color}66` : "none" }}
      >
        {isRange ? (
          <span>
            <CountUp target={a} run={inView} />
            <span className="mx-1 opacity-60">–</span>
            <CountUp target={b} run={inView} />
            {suffix}
          </span>
        ) : (
          <span>
            <CountUp target={a} run={inView} />
            {suffix}
          </span>
        )}
      </div>
      {caption && (
        <p className="font-body text-zinc-400 text-sm md:text-base leading-relaxed mt-4 max-w-xs">
          {caption}
        </p>
      )}
    </div>
  );
}

function CountUp({ target, run }) {
  const decimals = String(target).includes(".") ? 1 : 0;
  const num = parseFloat(target);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!run) return;
    const controls = animate(0, num, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [run, num]);
  return <>{display.toFixed(decimals)}</>;
}

// Horizontal animated bar chart.
// bars: [{ label, value, color, highlight }]
export function BarChart({ title, bars, max = 100, unit = "%", testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.35 });
  const [focus, setFocus] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setFocus((f) => (f + 1) % bars.length), 1400);
    return () => clearInterval(id);
  }, [inView, bars.length]);

  return (
    <div ref={ref} data-testid={testid} className="w-full">
      {title && (
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-6">{title}</p>
      )}
      <div className="space-y-5">
        {bars.map((b, i) => {
          const active = inView && i === focus;
          const col = b.color || "#00e5ff";
          return (
            <div key={b.label} className="flex items-center gap-4">
              <span
                className="font-body text-sm w-40 sm:w-52 shrink-0 text-right leading-tight transition-colors duration-300"
                style={{ color: active ? "#e4e4e7" : "#9ca3af" }}
              >
                {b.label}
              </span>
              <div className="flex-1 h-8 bg-white/5 rounded-sm overflow-hidden relative">
                <motion.div
                  className="h-full rounded-sm transition-shadow duration-500"
                  style={{
                    background: col,
                    opacity: active ? 1 : b.highlight ? 0.92 : 0.7,
                    boxShadow: active ? `0 0 22px ${col}aa` : "none",
                  }}
                  initial={{ width: 0 }}
                  animate={inView ? { width: `${(b.value / max) * 100}%` } : { width: 0 }}
                  transition={{ duration: 1.1, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
              <motion.span
                className="font-heading text-lg md:text-xl font-medium w-16 shrink-0"
                animate={{ scale: active ? 1.18 : 1, color: active ? col : "#e4e4e7" }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: "left center", display: "inline-block" }}
              >
                {b.value}
                {unit}
              </motion.span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
